/**
 * Products (2026-10-01): replaces the static Product "design shell".
 * Products tab — every product with its stage, delivery health (from the
 * projects/programs that build it), next delivery and key metrics.
 * Roadmap tab — those projects by quarter, from their real dates.
 * Ideas tab — the RICE-ranked backlog, one click into project intake.
 * Backend: app/services/products.py.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Box, Plus } from 'lucide-react'

import { Button } from '../components/ui/button'
import { IdeasTable } from '../components/product/IdeasTable'
import { HealthBadge, MetricLine, StageBadge } from '../components/product/productUi'
import { useProductRoles } from '../hooks/useProductRoles'
import { portfoliosService } from '../services/portfolios'
import {
  STAGE_LABEL, productsService, type ProductIdea, type ProductsOverview, type Roadmap, type Stage,
} from '../services/products'
import { apiErrorMessage } from '../utils/apiError'

type Tab = 'products' | 'roadmap' | 'ideas'
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'products', label: 'Products' }, { key: 'roadmap', label: 'Roadmap' }, { key: 'ideas', label: 'Ideas' },
]
const input = 'min-h-[44px] rounded-md border border-border bg-background px-3 text-sm text-foreground'

export default function ProductsPage() {
  const [params, setParams] = useSearchParams()
  const tab = (params.get('tab') as Tab) || 'products'
  const { canEdit, canSubmit } = useProductRoles()
  const [data, setData] = useState<ProductsOverview | null>(null)
  const [ideas, setIdeas] = useState<ProductIdea[]>([])
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showNew, setShowNew] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      const [ov, ids] = await Promise.all([productsService.overview(), productsService.ideas()])
      setData(ov)
      setIdeas(ids)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load products.'))
    }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (tab === 'roadmap' && !roadmap) {
      productsService.roadmap(4).then(setRoadmap).catch((err) => toast.error(apiErrorMessage(err, 'Could not load the roadmap.')))
    }
  }, [tab, roadmap])

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-foreground"><Box className="h-6 w-6 text-primary" /> Products</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            What you sell or run, how it’s doing, and the projects that are building it.
          </p>
        </div>
        {canEdit && tab === 'products' && (
          <Button onClick={() => setShowNew((v) => !v)} className="min-h-[44px] gap-2"><Plus className="h-4 w-4" /> New product</Button>
        )}
      </header>

      <nav className="flex gap-6 border-b border-border" aria-label="Product views">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setParams(t.key === 'products' ? {} : { tab: t.key })}
            aria-current={tab === t.key ? 'page' : undefined}
            className={`-mb-px min-h-[44px] border-b-2 px-1 text-sm ${tab === t.key ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}{t.key === 'ideas' && data?.summary.ideas_to_triage ? ` (${data.summary.ideas_to_triage} new)` : ''}
          </button>
        ))}
      </nav>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button>
        </div>
      )}
      {!data && !error && <p className="text-sm text-muted-foreground">Loading products…</p>}

      {data && tab === 'products' && (
        <>
          {showNew && <NewProduct onDone={() => { setShowNew(false); load() }} />}
          <ProductsGrid data={data} />
        </>
      )}
      {data && tab === 'roadmap' && (roadmap ? <RoadmapView roadmap={roadmap} /> : <p className="text-sm text-muted-foreground">Loading roadmap…</p>)}
      {data && tab === 'ideas' && (
        <IdeasTable ideas={ideas} products={data.products.map((p) => ({ id: p.id, name: p.name }))}
          canTriage={canEdit} canSubmit={canSubmit} onChanged={load} />
      )}
    </div>
  )
}

function ProductsGrid({ data }: { data: ProductsOverview }) {
  if (data.products.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
        <h2 className="text-lg font-semibold text-foreground">No products yet</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          Add the products you sell or run, then link the projects and programs that build them. Health, roadmap and
          delivery dates then come straight from that work.
        </p>
      </div>
    )
  }
  const s = data.summary
  return (
    <>
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Summary">
        <Kpi label="Products" value={String(s.products)} />
        <Kpi label="In development or launching" value={String((s.by_stage.development ?? 0) + (s.by_stage.launch ?? 0))} />
        <Kpi label="With delivery at risk" value={String(s.at_risk)} warn={s.at_risk > 0} />
        <Kpi label="Ideas to triage" value={String(s.ideas_to_triage)} />
      </section>
      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" aria-label="Products">
        {data.products.map((p) => (
          <Link key={p.id} to={`/product/${p.id}`}
            className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/50">
            <div className="flex items-start justify-between gap-2">
              <h2 className="font-semibold text-foreground">{p.name}</h2>
              <StageBadge stage={p.stage} />
            </div>
            {p.description && <p className="line-clamp-2 text-sm text-muted-foreground">{p.description}</p>}
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{p.open_projects} open project{p.open_projects === 1 ? '' : 's'}</span>
              {p.at_risk > 0 && <HealthBadge health="at_risk" />}
              {p.at_risk === 0 && p.watch > 0 && <HealthBadge health="watch" />}
              {p.at_risk === 0 && p.watch === 0 && p.open_projects > 0 && <HealthBadge health="on_track" />}
              {p.next_delivery && <span>· next delivery {p.next_delivery}</span>}
            </div>
            {p.metrics.length > 0 && (
              <div className="space-y-1 border-t border-border pt-2">
                {p.metrics.slice(0, 3).map((m) => <MetricLine key={m.id} m={m} />)}
              </div>
            )}
            <div className="mt-auto text-xs text-muted-foreground">
              {p.owner_name ? `Owner: ${p.owner_name}` : 'No owner'}{p.ideas_open ? ` · ${p.ideas_open} open idea${p.ideas_open === 1 ? '' : 's'}` : ''}
            </div>
          </Link>
        ))}
      </section>
    </>
  )
}

function Kpi({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${warn ? 'text-warning' : 'text-foreground'}`}>{value}</div>
    </div>
  )
}

function NewProduct({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [stage, setStage] = useState<Stage>('development')
  const [portfolioId, setPortfolioId] = useState<number | null>(null)
  const [portfolios, setPortfolios] = useState<Array<{ id: number; name: string }>>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    portfoliosService.list().then((list) => setPortfolios(list.map((p) => ({ id: p.id, name: p.name })))).catch(() => setPortfolios([]))
  }, [])

  const save = async () => {
    setBusy(true)
    try {
      const p = await productsService.create({ name: name.trim(), description: description.trim() || null, stage, portfolio_id: portfolioId })
      toast.success(`${p.name} added — now link the work that builds it`)
      onDone()
      navigate(`/product/${p.id}`)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not add the product.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-3">
      <label className="text-sm text-foreground">Name
        <input className={`${input} mt-1 w-full`} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="text-sm text-foreground">Stage
        <select className={`${input} mt-1 w-full`} value={stage} onChange={(e) => setStage(e.target.value as Stage)}>
          {(Object.keys(STAGE_LABEL) as Stage[]).map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
      </label>
      <label className="text-sm text-foreground">Portfolio
        <select className={`${input} mt-1 w-full`} value={portfolioId ?? ''} onChange={(e) => setPortfolioId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">None</option>
          {portfolios.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="text-sm text-foreground md:col-span-3">What it is
        <input className={`${input} mt-1 w-full`} value={description} maxLength={4000} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <div className="flex justify-end md:col-span-3">
        <Button onClick={save} disabled={busy || !name.trim()}>{busy ? 'Adding…' : 'Add product'}</Button>
      </div>
    </div>
  )
}

const BAR: Record<string, string> = {
  at_risk: 'bg-destructive/20 border-destructive/40 text-destructive',
  watch: 'bg-warning/20 border-warning/40 text-foreground',
  on_track: 'bg-success/15 border-success/40 text-foreground',
}

export function RoadmapView({ roadmap }: { roadmap: Roadmap }) {
  const n = roadmap.quarters.length
  if (roadmap.lanes.every((l) => l.items.length === 0)) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        Nothing on the roadmap yet. Link projects with start and end dates to a product and they appear here.
      </div>
    )
  }
  return (
    <section aria-label="Roadmap" className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <div className="min-w-[760px]">
          <div className="grid border-b border-border text-xs uppercase tracking-wide text-muted-foreground"
            style={{ gridTemplateColumns: `180px repeat(${n}, minmax(0, 1fr))` }}>
            <div className="px-3 py-2">Product</div>
            {roadmap.quarters.map((q) => <div key={q.key} className="border-l border-border px-3 py-2">{q.label}</div>)}
          </div>
          {roadmap.lanes.map((lane) => (
            <div key={lane.product_id} className="grid border-b border-border last:border-b-0"
              style={{ gridTemplateColumns: `180px repeat(${n}, minmax(0, 1fr))` }}>
              <div className="px-3 py-3">
                <Link to={`/product/${lane.product_id}`} className="font-medium text-foreground hover:underline">{lane.name}</Link>
                <div className="mt-1"><StageBadge stage={lane.stage} /></div>
              </div>
              <div className="relative grid gap-1 py-2" style={{ gridColumn: `2 / span ${n}`, gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
                {lane.items.length === 0 && <div className="col-span-full px-3 text-xs text-muted-foreground">No dated work in this window</div>}
                {lane.items.map((it) => (
                  <Link key={it.id} to={`/project/${it.id}`} title={`${it.start_date ?? '?'} → ${it.end_date ?? '?'}${it.health_reason ? ` · ${it.health_reason}` : ''}`}
                    className={`mx-1 truncate rounded border px-2 py-1 text-xs hover:opacity-80 ${BAR[it.health ?? 'on_track'] ?? BAR.on_track}`}
                    style={{ gridColumn: `${it.from_col + 1} / ${it.to_col + 2}` }}>
                    {it.name} · {it.progress}%
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Bars come from each project’s start and end dates; colour is its delivery health (the same check as the dashboard’s
        Needs attention). Change a project’s dates and the roadmap follows.
      </p>
    </section>
  )
}
